import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CompanyInfoService } from './info/company/company-info-service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RestockSearchComponent } from './purchase/restock/search/restock-search.component';
import { HomeComponent } from './home.component';
import { PublisherSearchCriteriaComponent } from './inventory/reference/search/criteria/publisher-search-criteria.component';
import { PurchaseOrderSearchComponent } from './purchase/order/search/purchase-order-search.component';
import { InvoiceSearchComponent } from './purchase/invoice/search/invoice-search.component';
import { InvoiceStatusComponent } from './purchase/invoice/invoice-status.component';
import { ReferenceSearchComponent } from './inventory/reference/search/reference-search.component';
import { SupplierSearchComponent } from './inventory/supplier/search/supplier-search.component';
import { RestockService } from './purchase/restock/restock.service';
import { RestockDetailComponent } from './purchase/restock/details/restock-details.component';
import { ReferenceSearchDialogComponent } from './inventory/reference/search/reference-search-dialog.component';
import { ReferenceService } from './inventory/reference/reference.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenreService } from './inventory/genre/genre.service';
import { AuthorService } from './inventory/author/author.service';
import { PublisherService } from './inventory/publisher/publisher.service';
import { ReferenceResultComponent } from './inventory/reference/search/reference-result.component';
import { GenreSearchCriteriaComponent } from './inventory/reference/search/criteria/genre-search-criteria.component';
import { AuthorSearchCriteriaComponent } from './inventory/reference/search/criteria/author-search-criteria.component';
import { SearchPagingComponent } from './util/search/paging/search-paging.component';
import { SelectableReferenceResultComponent } from './inventory/reference/search/selectable-reference-result.component';
import { SupplierService } from './inventory/supplier/supplier.service';
import { SupplierSearchCriteriaComponent } from './purchase/order/search/criteria/supplier-search-criteria.component';
import { PurchaseOrderService } from './purchase/order/purchase-order.service';
import { PurchaseOrderStatusComponent } from './purchase/order/purchase-order-status.component';
import { PurchaseOrderComponent } from './purchase/order/purchase-order.component';
import { RestockStatusComponent } from './purchase/restock/restock-status.component';
import { PurchaseOrderDetailsComponent } from './purchase/order/details/purchase-order-details.component';
import { PurchaseOrderNeedaComponent } from './purchase/order/needa/purchase-order-needa.component';
import { InvoiceService } from './purchase/invoice/invoice-service';
import { InvoiceDetailsComponent } from './purchase/invoice/details/invoice-details.component';
import { InvoiceNeedaComponent } from './purchase/invoice/needa/invoice-needa.component';
import { ReceptionDetailsComponent } from './inventory/reception/details/reception-details.component';
import { ReceptionSearchComponent } from './inventory/reception/search/reception-search.component';
import { ReceptionStatusComponent } from './inventory/reception/reception-status.component';
import { ReceptionService } from './inventory/reception/reception-service';
import { AuthInterceptor } from './auth-interceptor';
import { LogoutSuccessComponent } from './login/logout-success.component';
import { registerLocaleData } from '@angular/common';
import localeFrCA from '@angular/common/locales/fr-CA';
import { NeedaFileService } from './purchase/needa-file-service';
import { SupplierPickerComponent } from './inventory/supplier/supplier-picker/supplier-picker.component';
import { NewReferenceComponent } from './inventory/reference/new-reference/new-reference.component';
import { ReferencenDetailsComponent } from './inventory/reference/details/reference-details.component';
import { StringEntryComponent } from './util/string-entry.component';
import { StringArrayComponent } from './util/string-array.component';
import { ReferenceImageComponent } from './inventory/reference/reference-image/reference-image.component';
import { RmaStatusComponent } from './inventory/rma/status/rma-status.component';
import { RmaSearchComponent } from './inventory/rma/search/rma-search.component';
import { RmaSupplierSearchComponent } from './inventory/rma/search/search-supplier/rma-supplier-search.component';
import { RmaSupplierSearchCriteriaComponent } from './inventory/rma/search/criteria/supplier-search-criteria.component';
import { RmaService } from './inventory/rma/rma-service';
import { RmaDetailsComponent } from './inventory/rma/details/rma-details.component';
import { ReferenceInventoryComponent } from './inventory/reference/reference-inventory/reference-inventory.component';
import { DeliveryModeComponent } from './purchase/order/details/delivery-mode.component';
import { InvoiceTypeComponent } from './purchase/invoice/invoice-type/invoice-type.component';
import { InvoiceAccountingStatus } from './purchase/invoice/invoice-accounting-status/invoice-accounting-status.component';
import { QuickbookService } from './quickbook/quickbook-service';
import { UpdateSupplierReferenceDialogComponent } from './inventory/reference/update-supplier-reference-dialog/update-supplier-reference-dialog.component';
import { SelectSupplierComponent } from './purchase/supplier/select-supplier/select-supplier.component';
import { NewReceptionComponent } from './inventory/reception/search/new-reception/new-reception.component';
import { NewPurchaseOrderComponent } from './purchase/order/search/new-purchase-order/new-purchase-order.component';
import { QuickbookOverviewComponent } from './quickbook/quickbook-overview/quickbook-overview.component';
import { SystemOverviewComponent } from './system/system-overview.component';
import { ShopifyOverviewComponent } from './shopify/shopify-overview/shopify-overview.component';
import { ShopifyService } from './shopify/shopify-service';
import { LesLibrairesOverviewComponent } from './les-libraires/les-libraires-overview/les-libraires-overview.component';
import { LesLibrairesService } from './les-libraires/les-libraires-service';
import { GiftCardAlertService } from './gift-card-alert/gift-card-alert-service';
import { GiftCardAlertComponent } from './gift-card-alert/gift-card-alerts/gift-card-alerts.component';
import { InventoryAdjustmentService } from './inventory/adjustment/inventory-adjustment.service';
import { InventoryAdjustmentComponent } from './inventory/adjustment/inventory-adjustment/inventory-adjustment.component';
import { PriceChangesComponent } from './inventory/price-change/price-changes/price-changes.component';
import { PriceChangeService } from './inventory/price-change/price-change-service';
import { ReceptionDetailComponent } from './inventory/reception/detail/reception-detail.component';
import { SwitchSupplierDialogComponent } from './inventory/reference/switch-supplier-reference-dialog/switch-supplier-dialog.component';

registerLocaleData(localeFrCA, 'fr-CA');

const oktaConfig = {
  issuer: 'https://dev-48565448.okta.com/oauth2/auslgtemgyjHX904W5d6',
  clientId: '0oalgtb81s6U5JphE5d6',

  redirectUri: window.location.origin + '/login/callback',
  scopes: ['openid', 'groups', 'email'],
}

@NgModule({
  declarations: [
    AuthorSearchCriteriaComponent,
    AppComponent,
    DeliveryModeComponent,
    LogoutSuccessComponent,
    HomeComponent,
    InventoryAdjustmentComponent,
    InvoiceAccountingStatus,
    InvoiceDetailsComponent,
    InvoiceNeedaComponent,
    InvoiceSearchComponent,
    InvoiceStatusComponent,
    InvoiceTypeComponent,
    GiftCardAlertComponent,
    GenreSearchCriteriaComponent,
    LesLibrairesOverviewComponent,
    NewPurchaseOrderComponent,
    NewReferenceComponent,
    NewReceptionComponent,
    PriceChangesComponent,
    PublisherSearchCriteriaComponent,
    PurchaseOrderComponent,
    PurchaseOrderDetailsComponent,
    PurchaseOrderNeedaComponent,
    PurchaseOrderSearchComponent,
    PurchaseOrderStatusComponent,
    QuickbookOverviewComponent,
    ReceptionDetailComponent,
    ReceptionDetailsComponent,
    ReceptionSearchComponent,
    ReceptionStatusComponent,
    ReferencenDetailsComponent,
    ReferenceImageComponent,
    ReferenceInventoryComponent,
    ReferenceResultComponent,
    ReferenceSearchComponent,
    ReferenceSearchDialogComponent,
    RestockDetailComponent,
    RestockSearchComponent,
    RestockStatusComponent,
    RmaDetailsComponent,
    RmaStatusComponent,
    RmaSearchComponent,
    RmaSupplierSearchComponent,
    RmaSupplierSearchCriteriaComponent,
    SearchPagingComponent,
    SelectSupplierComponent,
    SelectableReferenceResultComponent,
    ShopifyOverviewComponent,
    StringArrayComponent,
    StringEntryComponent,
    SupplierPickerComponent,
    SupplierSearchComponent,
    SupplierSearchCriteriaComponent,
    SwitchSupplierDialogComponent,
    SystemOverviewComponent,
    UpdateSupplierReferenceDialogComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    HttpClientModule,
    NgbModule,
    OktaAuthModule,
    ReactiveFormsModule,
  ],
  providers: [
    { provide: OKTA_CONFIG, useValue: oktaConfig },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    AuthorService,
    CompanyInfoService,
    GiftCardAlertService,
    GenreService,
    InventoryAdjustmentService,
    InvoiceService,
    LesLibrairesService,
    NeedaFileService,
    PriceChangeService,
    PublisherService,
    PurchaseOrderService,
    QuickbookService,
    ReceptionService,
    ReferenceService,
    RestockService,
    RmaService,
    ShopifyService,
    SupplierService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
